import * as React from "react";
import {
  IStackTokens,
  Stack,
  TextField,
  Toggle,
  DefaultButton,
  PrimaryButton,
  ITag,
  Label,
  TagPicker,
  IBasePickerSuggestionsProps,
  Text,
} from "office-ui-fabric-react";
import { IAdmin } from "../../vm";
import CustomDrawer from "../common/CustomDrawer";
import { Formik, FormikErrors } from "formik";
import { EMAIL_REGEX } from "../common/Constants";
import {
  updateDataCenterManager,
  addDataCenterManager,
} from "../../services/datacenter/DataCenterManagerService";
import { ToastContext } from "../common/ToastProvider";
import { isValidPassword } from "../../services/UtilService";
import PasswordPolicy from "../common/PasswordPolicy";

export interface ManageDataCenterManagerProps {
  data?: IAdmin;
  closeManageDialog: any;
  handleLoading: any;
  datacenterId: string;
  datacenters: ITag[];
}

const ManageDataCenterManager: React.FC<ManageDataCenterManagerProps> = ({
  datacenterId,
  handleLoading,
  closeManageDialog,
  data,
  datacenters
}) => {
  const [state, setState] = React.useState({
    dataCenterManager: {
      email: "",
      name: "",
      password: "",
      isEnabled: true,
      role: 1,
      datacenterIds: [datacenterId],
      ...data,
    },
    isLoading: false,
  } as {
    dataCenterManager: IAdmin;
    isLoading: boolean;
  });

  const closeAddDialog = (data?: IAdmin) => {
    closeManageDialog(data);
  };

  const wrapStackTokens: IStackTokens = { childrenGap: 16 };
  const { showToast } = React.useContext(ToastContext);

  const pickerSuggestionsProps: IBasePickerSuggestionsProps = {
    noResultsFoundText: "No datacenter found",
  };

  const filterSuggestedTags = (filterText: string, tagList: ITag[]): ITag[] => {
    let allDataCenterList = [...datacenters];

    return filterText
      ? allDataCenterList.filter(
          (tag) =>
            tag.name.toLowerCase().indexOf(filterText.toLowerCase()) === 0 &&
            !listContainsTagList(tag, tagList)
        )
      : [];
  };

  const showAllTags = (selectedItems?: ITag[] | undefined) => {
    let allDataCenterList = [...datacenters];
    return removeDuplicates(allDataCenterList, selectedItems);
  };

  function removeDuplicates(tagList: ITag[], tags?: ITag[]) {
    return tagList.filter((tag) => !listContainsTagList(tag, tags));
  }

  const listContainsTagList = (tag: ITag, tagList?: ITag[]) => {
    if (!tagList || !tagList.length || tagList.length === 0) {
      return false;
    }
    return tagList.some((compareTag) => compareTag.key === tag.key);
  };

  return (
    <React.Fragment>
      <CustomDrawer
        title={
          state.dataCenterManager._id
            ? "Update Datacenter Manager"
            : "Add Datacenter Manager"
        }
        handelOpen={true}
        onClose={() => closeAddDialog()}
      >
        <Stack>
          <Formik
            enableReinitialize
            initialValues={state.dataCenterManager}
            validate={(values: any) => {
              let errors: FormikErrors<IAdmin> = {};
              if (!values.name) {
                errors.name = "Required";
              }
              if (!values._id && !values.password) {
                errors.password = "Required";
              }
              if (
                !values._id &&
                values.password &&
                !isValidPassword(values.password, false)
              ) {
                errors.password = "Please enter valid password.";
              }
              if (!values.email) {
                errors.email = "Required";
              }
              if (values.email && !EMAIL_REGEX.test(values.email)) {
                errors.email = "Invalid email address";
              }
              if (values.datacenterIds.length === 0) {
                errors.datacenterIds = "Select atleast one";
              }
              return errors;
            }}
            onSubmit={async (values: IAdmin, { setSubmitting }) => {
              let obj = { ...values };
              let result;
              handleLoading();
              if (state.dataCenterManager._id) {
                result = await updateDataCenterManager(obj);
              } else {
                result = await addDataCenterManager(obj);
              }
              handleLoading();
              if (result.success) {
                closeAddDialog(result.data);
                showToast(
                  `Datacenter manager ${
                    state.dataCenterManager._id ? "updated" : "created"
                  } successfully`,
                  "success"
                );
              } else {
                showToast(result?.message ||
                  `Error while ${
                    state.dataCenterManager._id ? "updating" : "adding"
                  } an datacenter manager`,
                  "error"
                );
              }
              setSubmitting(false);
            }}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              setFieldValue,
            }) => (
              <form onSubmit={handleSubmit}>
                <Stack wrap tokens={wrapStackTokens}>
                  <Stack.Item className="custom-textfield">
                    <TextField
                      name="name"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.name}
                      label="Name"
                      errorMessage={
                        errors.name && touched.name ? errors.name : ""
                      }
                      className="custom-textfield"
                    />
                  </Stack.Item>
                  <Stack.Item className="custom-textfield">
                    <TextField
                      name="email"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.email}
                      label="Email"
                      errorMessage={
                        errors.email && touched.email ? errors.email : ""
                      }
                    />
                  </Stack.Item>
                  {state.dataCenterManager._id === undefined && (
                    <>
                      <Stack.Item className="custom-textfield">
                        <TextField
                          name="password"
                          type="password"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.password}
                          label="Password"
                          errorMessage={
                            errors.password && touched.password
                              ? errors.password
                              : ""
                          }
                        />
                      </Stack.Item>
                      {errors.password && touched.password && values.password && (
                        <Stack.Item>
                          <PasswordPolicy password={values.password} isAdmin={false} />
                        </Stack.Item>
                      )}
                    </>
                  )}
                  <Stack.Item className="custom-picker">
                      <Label>Datacenter</Label>
                      <TagPicker
                        removeButtonAriaLabel="Remove"
                        selectedItems={datacenters.filter((d) => values.datacenterIds.includes(d.key as string))}
                        onResolveSuggestions={filterSuggestedTags as any}
                        pickerSuggestionsProps={pickerSuggestionsProps}
                        onEmptyResolveSuggestions={showAllTags}
                        inputProps={{
                          placeholder: "Search a particular datacenter",
                        }}
                        onChange={(items: any) => {
                          let datacenterIds: string[] = [];
                          items.forEach((i: any) => {
                            datacenterIds.push(i.key);
                          });
                          setFieldValue("datacenterIds", datacenterIds);
                        }}
                      />
                      {errors.datacenterIds && touched.datacenterIds && (
                        <Text className="error-msg">
                          {errors.datacenterIds}
                        </Text>
                      )}
                  </Stack.Item>
                  <Stack.Item>
                    <Toggle
                      defaultChecked={values.isEnabled}
                      onText="Enabled"
                      offText="Disabled"
                      onChange={(e, v) => {
                        setFieldValue("isEnabled", v);
                      }}
                    />
                  </Stack.Item>
                </Stack>
                <Stack.Item>
                  <Stack
                    horizontal
                    wrap
                    tokens={wrapStackTokens}
                    horizontalAlign="end"
                  >
                    <DefaultButton
                      text="Cancel"
                      onClick={() => closeAddDialog()}
                      className="borderless-btn"
                    />
                    <PrimaryButton
                      type="submit"
                      text={state.dataCenterManager._id ? "Update" : "Add"}
                    />
                  </Stack>
                </Stack.Item>
              </form>
            )}
          </Formik>
        </Stack>
      </CustomDrawer>
    </React.Fragment>
  );
};

export default ManageDataCenterManager;
